import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import i18n from 'util/i18n';

import { HISTORY, HISTORY_EDUCATION } from 'config/formSections/history';
import { formConfig } from 'config/forms';

import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { HistoryEducationModel } from 'forms/sections/history/education/types';
import { FormTypes, SF852013 } from 'constants/formTypes';
import { EmptyYesOrNo, EmptyList } from 'forms/shared/models';
import { EmptyBranch } from 'forms/shared/validators/models/branch/instance';
import { SubsectionConnectorProps } from 'components/Section/shared/types';
import { CommonOnError, CommonOnUpdate } from 'types/props';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Comments from 'components/Form/Comments';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import { educationRequiresPhysicalLocationAndReference } from 'models/education';
import EducationSummaryProgress from './EducationSummaryProgress';
import ConnectedEducation from './Education';

const sectionConfig = {
  key: HISTORY_EDUCATION.key,
  section: HISTORY.name,
  store: HISTORY.store,
  subsection: HISTORY_EDUCATION.name,
};

interface Props extends SubsectionConnectorProps {
  Education: Partial<HistoryEducationModel>
  data: Partial<HistoryEducationModel>
  formType: FormTypes
  inReview: boolean
  onUpdate: CommonOnUpdate
  onError: CommonOnError

  // TODO: Remove this prop.
  dispatch: Dispatch

  errors: string[]
}

export class EducationWrapper extends React.Component<Props> {
  static defaultProps = {
    Education: {
      HasAttended: '',
      HasDegree10: '',
      List: { items: [] },
      AttendedComments: {},
      HasDegreeComments: {},
      SummaryComments: {},
    },
    formType: '',
    data: {},
    inReview: false,
    // TODO: Remove
    onUpdate: () => { },
    // TODO: Remove
    dispatch: () => { },
    errors: [],
  };

  section: string;

  subsection: string;

  store: string;

  storeKey: string;

  constructor(props: Props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
  }

  handleError = (value, arr) => {
    const arr2 = arr.map((err) => ({
      code: err.code,
      valid: err.valid,
      uid: err.uid,
    }));

    // Take the original and concatenate our new error values to it
    return this.props.onError(value, arr2);
  };

  updateBranchAttendance = (values: HistoryEducationModel['HasAttended']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    education.HasAttended = values;
    education.HasDegree10 = values.value === 'No' ? education.HasDegree10 : EmptyYesOrNo();
    education.HasDegreeComments = values.value === 'No' ? education.HasDegreeComments : {};
    education.List = values.value === 'No' ? EmptyList() : education.List;
    if (values.value === 'No' && education.List) {
      education.List.branch = EmptyBranch();
    }

    onUpdate('Education', education);
  };

  updateBranchDegree10 = (values: HistoryEducationModel['HasDegree10']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    const shouldClearData = values.value === 'No' && education.HasAttended?.value === 'No';
    education.HasDegree10 = values;
    education.ListDegrees = shouldClearData ? EmptyList() : education.ListDegrees;

    onUpdate('Education', education);
  };

  updateAttendedComments = (values: HistoryEducationModel['AttendedComments']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    education.AttendedComments = values;

    onUpdate('Education', education);
  };

  updateHasDegreeComments = (values: HistoryEducationModel['HasDegreeComments']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    education.HasDegreeComments = values;

    onUpdate('Education', education);
  };

  updateSummaryComments = (values: HistoryEducationModel['SummaryComments']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    education.SummaryComments = values;

    onUpdate('Education', education);
  };

  handleUpdate = (values: HistoryEducationModel['List']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    education.List = values;
    onUpdate('Education', education);
  };

  // PVQ Only
  handleUpdateDegreeList = (values: HistoryEducationModel['ListDegrees']) => {
    const { Education, onUpdate } = this.props;
    const education = Education ? cloneDeep(Education) : {};
    education.ListDegrees = values;
    onUpdate('Education', education);
  };

  render() {
    const {
      Education, formType, inReview, errors, data
    } = this.props;

    const years = formType && formConfig[formType] && formConfig[formType].HISTORY_EDUCATION_YEARS;

    const hasAttendedSchool = Education?.HasAttended?.value === 'Yes';
    const hasDegree = Education?.HasDegree10?.value === 'Yes';

    const isPvq = isPVQForm(formType);

    const reviewProps = inReview
      ? {
        realtime: true,
        overrideInitial: true,
        scrollIntoView: false,
        required: true,
      }
      : {
        scrollToTop: 'scrollToHistory',
      };

    return isPvq ? (
      <div
        className="section-content education"
        data-section={HISTORY.key}
        data-subsection={HISTORY_EDUCATION.key}
      >
        {!inReview && (
          <h1 className="section-header">{i18n.t('history.pvq.education.summary.title')}</h1>
        )}

        <Field
          title={i18n.t('history.pvq.education.title')}
          titleSize={inReview ? 'h4' : 'h3'}
          optional
          className="no-margin-bottom"
        >
          { i18n.m('history.pvq.education.info') }
          {i18n.m('history.pvq.education.info2')}
        </Field>

        <Branch
          attribute="HasAttended"
          name="branch_school"
          {...Education.HasAttended}
          help="history.pvq.education.help.attendance"
          helpMessage={i18n.m('history.pvq.education.help.attendance.message', {
            years,
          })}
          label={i18n.t('history.pvq.education.label.attendance')}
          labelSize={inReview ? 'h3' : 'h4'}
          warning
          onUpdate={this.updateBranchAttendance}
          required={inReview}
          onError={this.handleError}
        />

        {hasAttendedSchool && (
          <div>
            <span id="scrollToHistory" />

            {!inReview && <EducationSummaryProgress isHistoricalDegree={false} items={data?.List?.items} errors={errors} formType={this.props.formType} />}

            <ConnectedEducation
              onUpdate={this.handleUpdate}
              totalYears={years}
              formType={this.props.formType}
              isHistoricalDegree={false}
              {...reviewProps}
            />
          </div>
        )}
        {!hasAttendedSchool && (
          <Branch
            attribute="HasDegree10"
            name="branch_degree10"
            {...Education.HasDegree10}
            help="history.pvq.education.help.degree5"
            label={i18n.t('history.pvq.education.label.degree5')}
            labelSize={inReview ? 'h3' : 'h4'}
            warning
            onUpdate={this.updateBranchDegree10}
            required={inReview}
          />
        )}
        {hasDegree && (
          <div>
            <span id="scrollToHistory" />

            {!inReview && <EducationSummaryProgress isHistoricalDegree items={data?.ListDegrees?.items} errors={errors} formType={this.props.formType} />}

            <ConnectedEducation
              onUpdate={this.handleUpdateDegreeList}
              totalYears={years}
              {...reviewProps}
              formType={this.props.formType}
              isHistoricalDegree
            />
          </div>
        )}
      </div>
    ) : (
      <div
        className="section-content education"
        data-section={HISTORY.key}
        data-subsection={HISTORY_EDUCATION.key}
      >
        {!inReview && (
          <h1 className="section-header">{i18n.t('history.education.summary.title')}</h1>
        )}

        <Field
          title={i18n.t('history.education.title')}
          titleSize={inReview ? 'h4' : 'h3'}
          optional
          className="no-margin-bottom"
        >
          {this.props.formType === SF852013 ? i18n.m('history.education.infoSF852013') : i18n.m('history.education.info')}
        </Field>

        <Branch
          attribute="HasAttended"
          name="branch_school"
          {...Education.HasAttended}
          help="history.education.help.attendance"
          helpMessage={i18n.m('history.education.help.attendance.message', {
            years,
          })}
          label={i18n.t('history.education.label.attendance', { years })}
          labelSize={inReview ? 'h3' : 'h4'}
          warning={false}
          onUpdate={this.updateBranchAttendance}
          required={inReview}
          onError={this.handleError}
        />
        <Comments
          title={i18n.t('history.education.label.attendedcomments')}
          name="Attended Comments"
          className="optional-comments"
          attribute="AttendedComments"
          {...this.props.AttendedComments}
          onUpdate={this.updateAttendedComments}
          required={false}
        />

        <Show when={Education?.HasAttended?.value === 'No'}>
          <Branch
            attribute="HasDegree10"
            name="branch_degree10"
            {...Education.HasDegree10}
            help="history.education.help.degree10"
            label={i18n.t('history.education.label.degree10', { years })}
            labelSize={inReview ? 'h3' : 'h4'}
            warning
            onUpdate={this.updateBranchDegree10}
            required={inReview}
          />
          <Comments
            title={i18n.t('history.education.label.hasdegreecomments')}
            name="HasDegreeComments"
            className="optional-comments"
            attribute="HasDegreeComments"
            {...this.props.HasDegreeComments}
            onUpdate={this.updateHasDegreeComments}
            required={false}
          />
        </Show>

        <Show when={hasAttendedSchool || hasDegree}>
          <div>
            <span id="scrollToHistory" />

            {!inReview && <EducationSummaryProgress items={data?.List?.items} errors={errors} />}

            <ConnectedEducation onUpdate={this.handleUpdate} totalYears={years} {...reviewProps} />
          </div>
          <Comments
            title={i18n.t('history.education.label.summarycomments')}
            name="Summary Comments"
            className="optional-comments"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

export default connectSubsection(EducationWrapper, sectionConfig);
